<script setup lang="ts">
/** Front elevation (port of pack.js elevationSVG): parts selectable, openings right, datums left. */
import { computed } from 'vue';
import { elevationModel, type ElevationSource } from '@/export';
import { ui } from '@/app/state';
import { hover, select } from '@/app/navigation';

const props = withDefaults(defineProps<{ build: ElevationSource; interactive?: boolean }>(), { interactive: true });
const m = computed(() => elevationModel(props.build));
const vb = computed(() => m.value.viewBox.join(' '));
const fs = computed(() => m.value.fontSize);
function pick(id: string) { if (props.interactive) select(ui.sel === id ? null : id); }
function over(id: string | null) { if (props.interactive && id !== ui.hov) hover(id); }
</script>

<template>
  <svg class="elev" :viewBox="vb" role="img" :aria-label="m.ariaLabel" @pointerleave="over(null)">
    <rect
      v-for="bk in m.backs"
      :key="bk.id"
      class="el-back"
      :class="{ 'is-sel': interactive && ui.sel === bk.id }"
      :data-elev-part="interactive ? bk.id : undefined"
      :x="bk.x"
      :y="bk.y"
      :width="bk.width"
      :height="bk.height"
      @click="pick(bk.id)"
      @pointerover="over(bk.id)"
    ><title>{{ bk.label }}</title></rect>
    <rect
      v-for="p in m.parts"
      :key="p.id"
      class="el-part"
      :class="interactive ? { 'is-sel': ui.sel === p.id, 'is-hov': ui.hov === p.id } : undefined"
      :data-elev-part="interactive ? p.id : undefined"
      :x="p.x"
      :y="p.y"
      :width="p.width"
      :height="p.height"
      @click="pick(p.id)"
      @pointerover="over(p.id)"
    ><title>{{ p.label }}</title></rect>
    <template v-for="(o, i) in m.openings" :key="`o${i}`">
      <g v-if="o.inside" class="el-op el-op--in">
        <text :x="o.textX" :y="o.textY" text-anchor="middle" dominant-baseline="central" :font-size="fs * 0.8">{{ o.text }}</text>
      </g>
      <g v-else class="el-op">
        <line :x1="o.x" :x2="o.x" :y1="o.yTop" :y2="o.yBottom" />
        <line :x1="o.tickX1" :x2="o.tickX2" :y1="o.yTop" :y2="o.yTop" />
        <line :x1="o.tickX1" :x2="o.tickX2" :y1="o.yBottom" :y2="o.yBottom" />
        <text :x="o.textX" :y="o.textY" dominant-baseline="central" :font-size="fs">{{ o.text }}</text>
      </g>
    </template>
    <g v-for="dt in m.datums" :key="`d${dt.shelfNo}`" class="el-datum">
      <line :x1="dt.x1" :x2="dt.x2" :y1="dt.y" :y2="dt.y" />
      <text :x="dt.textX" :y="dt.y" dominant-baseline="central" text-anchor="end" :font-size="fs * 0.92">{{ dt.text }}</text>
    </g>
    <g class="el-dim">
      <line x1="0" :x2="m.W" :y1="m.width.y" :y2="m.width.y" />
      <line x1="0" x2="0" :y1="m.width.tickY1" :y2="m.width.tickY2" />
      <line :x1="m.W" :x2="m.W" :y1="m.width.tickY1" :y2="m.width.tickY2" />
      <text :x="m.width.textX" :y="m.width.textY" text-anchor="middle" :font-size="fs">{{ m.width.text }}</text>
      <text :x="m.width.floorX" :y="m.width.floorY" text-anchor="end" dominant-baseline="central" :font-size="fs * 0.8" class="el-cap">floor</text>
    </g>
  </svg>
</template>
